import React, { FC, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { BIO_CUSTOM_PROPERTIES_INT32 } from "../../constants/biometric-fields-names";
import { CustomFieldBuilder } from "../../containers/utilities/CustomFieldBuilder";
import { useLocale } from "../../contexts/localeContext";
import { DynamicFieldDescriptor } from "../../models/DynamicFieldDescriptor";
import { getPresetInputsForBiometricSettings } from "../../services/biometricPresetService";
import ActivityFrame from "../activity-frame/activity-frame";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { useDialog } from "../dialog/DialogContext";
import Tooltip from "../Tooltip/Tooltip";

interface IdemiaAdvancedContentProps {
    presetId: string
}

export const IdemiaAdvancedCard: FC<IdemiaAdvancedContentProps> = ({ presetId }) => {
    const nameAccessor = 'name';
    const formik = useFormikContext();
    const [hasError, setHasError] = useState(false);
    const { translate } = useLocale();
    const customFieldBuilder: CustomFieldBuilder = new CustomFieldBuilder(formik, translate);
    const [busy, setBusy] = useState(false);
    const [supportedFields, setSupportedFields] = useState<DynamicFieldDescriptor[]>([]);
    const [toggleDefSetting, setToggleDefSetting] = useState(false);
    const idemiaAdvancedFields = [BIO_CUSTOM_PROPERTIES_INT32];
    const dialog = useDialog();

    // Provide a pre-defined value for Custom Properties Int32 which the user can choose to apply
    const accessorCustomPropertiesInt32 = "custom.BIO_CUSTOM_PROPERTIES_INT32.value";
    const predefinedPropInt32Val = "controller_feedback.feedback_lines | 2; remote_msg_conf.feedback_interface | 3; controller_feedback.denied_pulse_interval | 0; controller_feedback.denied_pulse_width | 500; controller_feedback.granted_pulse_interval | 0; controller_feedback.granted_pulse_width | 500";
    const [currentPropInt32Val, setCurrentPropInt32Val] = useState<string>(""); // Store the current value before toggling

    useEffect(() => {
        formik.validateForm();
    }, [(formik.values as any).custom])

    useEffect(() => {
        const supportedFieldAccessors = supportedFields?.map(field => field.systemName) ?? new Array(0);

        if (formik.errors[nameAccessor]
            || (supportedFieldAccessors.length > 0 && Object.keys(formik.errors as any).length > 0)) {
            setHasError(true)
        }
        else {
            setHasError(false)
        }
    }, [formik.errors]);

    useEffect(() => {
        reloadPage();
    }, [])

    const reloadPage = async () => {
        try {
            setBusy(true);
            const settingData: any = await getPresetInputsForBiometricSettings(presetId, idemiaAdvancedFields, translate);

            if (!!presetId) {
                const currentSupportedFields = settingData.elementFields;
                setSupportedFields(currentSupportedFields);
            }
            else {
                setSupportedFields(new Array<any>(0));
            }
        }
        catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }
        finally {
            setBusy(false);
        }
    }

    /**
     * Handle toggle change for Custom Properties Int32 advanced setting.
     * This function updates the Formik value for the setting based on the toggle state.
     */
    const handleToggle = () => {
        setToggleDefSetting((prev) => {
            const newValue = !prev;

            // Update Formik value for "Custom Properties Int32" when toggled on. 
            // Store the current value before changing it
            if (newValue) {
                setCurrentPropInt32Val((formik.values as any).custom.BIO_CUSTOM_PROPERTIES_INT32.value);
                formik.setFieldValue(accessorCustomPropertiesInt32, predefinedPropInt32Val);
            }
            // Restore the previous value when toggled off
            else {
                formik.setFieldValue(accessorCustomPropertiesInt32, currentPropInt32Val ?? "");
            }
            return newValue;
        });
    };

    const createFields = () => {
        var arrayFields = [] as any[];
        supportedFields?.filter(field => {
            if (idemiaAdvancedFields.includes(field.systemName)) {
                arrayFields.push(field);
            }
        });

        arrayFields.sort((a, b) => a.settingTypeOrdinal - b.settingTypeOrdinal);

        // Label for the toggle
        const toggleLabel = translate.byKey(TranslationKeys.applyRecommendedDefaultSettings);

        // Fields rendering with toggle injected after the matching field
        const fieldsWithToggle: JSX.Element[] = [];
        const fields = arrayFields?.reduce((formFields, field) =>
            customFieldBuilder.buildFormField(formFields, field, false, false, undefined, true),
            new Array(0));
        fields?.forEach(fieldBundle => {
            let fieldAccessor = "";

            // Try to get accessor from the fieldBundle
            if (fieldBundle.length === 1 && fieldBundle[0].content.props.accessor) {
                fieldAccessor = `${fieldBundle[0].content.props.accessor}`;
            }

            // Render the field
            fieldsWithToggle.push(
                <div className="flex flex-row pt-3" key={fieldAccessor || Math.random()}>
                    {fieldBundle.length === 1
                        ? fieldBundle[0].span
                            ? fieldBundle[0].content
                            : <>{fieldBundle[0].content}
                                <div className="flex flex-col flex-1 pl-3"></div></>
                        : fieldBundle.map(field => field.content)}
                </div>
            );
            // If this field matches the accessor, inject the toggle below it
            if (fieldAccessor === accessorCustomPropertiesInt32) {
                fieldsWithToggle.push(

                    <div className="flex flex-row items-center mb-2" key="toggle-row">
                        <Tooltip text={translate.byKey(TranslationKeys.idemiaRecommendedCustomPropTooltip)} direction="bottom" show={true}>
                            <input
                                type="checkbox"
                                checked={toggleDefSetting}
                                onChange={handleToggle}
                                className="mr-2"
                                id="advancedFieldsToggle"
                            />

                            <label htmlFor="advancedFieldsToggle">{toggleLabel}</label>
                        </Tooltip >
                    </div>

                );
            }
        });

        return <>{fieldsWithToggle}</>;
    }

    return <ActivityFrame busy={busy} className={`${busy ? 'mt-5 w-full h-full' : ''} flex flex-col flex-grow bg-white`}>
        {createFields()}
    </ActivityFrame>
}

export default IdemiaAdvancedCard;
